import React, { useState, useRef, useEffect, useCallback } from "react";
import { connect } from 'react-redux';
import { Formik } from 'formik';

import { Button, SVGIcon } from '../../../../components/custom-essentials';
import { BrowserTabTitle, LoadingOverlay, TooltipWrapper } from '../../../../components/display';
import { FormikControl, checkResponses } from '../../../../components/form';
import { getDataArray } from '../../../../components/functions';
import { Table } from '../../../../components/table';
import { Socket } from '../../../../components/ws';
import BookModal from './BookModal';

import { fetchBooksAll, createBook, updateBook, deleteBook } from '../../../../actions';

const pageTitle = 'Books';

const defaultSorted = { dataField: 'title', order: 'asc' };

function Books(props) {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => (mounted.current = false);
    });
    const formRef = useRef();

    const [hasLoaded, setHasLoaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [books, setBooks] = useState([]);
    const [filteredBooks, setFilteredBooks] = useState([]);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedBook, setSelectedBook] = useState(null);

    const { fetchBooksAll, createBook, updateBook, deleteBook } = props;

    const handleOpenModal = (mode, book = null) => {
        setModalMode(mode);
        setSelectedBook(book);
        setShowModal(true);
    };

    const handleCloseModal = (shouldRefresh = false) => {
        setShowModal(false);
        setSelectedBook(null);
        if (shouldRefresh) {
            refreshData();
        }
    };

    const filterBooks = useCallback((unfilteredBooks, filterQuery) => {
        if (!filterQuery || filterQuery.trim() === '') {
            setFilteredBooks(unfilteredBooks);
            return;
        }
        const query = filterQuery.toLowerCase().trim();
        const filtered = unfilteredBooks.filter(b => {
            return (
                (b.title && b.title.toLowerCase().includes(query)) ||
                (b.author && b.author.toLowerCase().includes(query)) ||
                (b.isbn && b.isbn.toLowerCase().includes(query))
            );
        });
        setFilteredBooks(filtered);
    }, []);

    const refreshData = useCallback(() => {
        (async function refresh() {
            if (loading) return;
            if (mounted.current) setLoading(true);

            try {
                const booksRes = await fetchBooksAll();
                const isApiError = checkResponses(booksRes);
                if (isApiError) {
                    if (mounted.current) {
                        setApiError('Error fetching data from the server. Please try again later.');
                        setLoading(false);
                        setHasLoaded(true);
                    }
                    return;
                } else setApiError(false);

                const newBooks = getDataArray(booksRes);
                const { filterQuery } = formRef.current?.values || { filterQuery: '' };

                if (mounted.current) {
                    setBooks(newBooks);
                    filterBooks(newBooks, filterQuery);
                    setHasLoaded(true);
                    setLoading(false);
                }
            } catch (error) {
                console.error('[BOOKS] Error:', error);
                if (mounted.current) {
                    setApiError('Error fetching data. Please try again later.');
                    setLoading(false);
                    setHasLoaded(true);
                }
            }
        })();
    }, [mounted, loading, fetchBooksAll, filterBooks]);

    useEffect(() => {
        refreshData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getColumns = () => [
        { dataField: 'title', text: 'Title', sort: true },
        { dataField: 'author', text: 'Author', sort: true },
        { dataField: 'isbn', text: 'ISBN', sort: true },
        {
            dataField: 'active',
            text: 'Status',
            sort: true,
            formatter: (row) => (
                <span className={parseInt(row.active) === 1 ? 'text-green-600' : 'text-gray-400'}>
                    {parseInt(row.active) === 1 ? 'Active' : 'Inactive'}
                </span>
            )
        },
        {
            dataField: 'actions',
            text: 'Actions',
            formatter: (row) => (
                <div className="flex flex-row gap-x-2">
                    <TooltipWrapper tooltipText="Edit book">
                        <Button
                            color="lte-mpLBlue"
                            variant="icon"
                            onClick={() => handleOpenModal('edit', row)}
                        >
                            <SVGIcon fileName="pencil" />
                        </Button>
                    </TooltipWrapper>
                    <TooltipWrapper tooltipText="Delete book">
                        <Button
                            color="lte-mpLRed"
                            variant="icon"
                            onClick={() => handleOpenModal('delete', row)}
                        >
                            <SVGIcon fileName="trash" />
                        </Button>
                    </TooltipWrapper>
                </div>
            )
        }
    ];

    return (
        <div className="page-box">
            <BrowserTabTitle>{pageTitle}</BrowserTabTitle>
            {loading && <LoadingOverlay />}
            <div className="card">
                <Formik
                    enableReinitialize
                    initialValues={{ filterQuery: '' }}
                    innerRef={formRef}
                    onSubmit={() => null}
                >
                    {formik => (
                        <form onSubmit={formik.handleSubmit}>
                            <div className="flex flex-row justify-between items-center mb-4">
                                <h2>{pageTitle}</h2>
                                <Button
                                    color="lte-mpLBlue"
                                    onClick={() => handleOpenModal('create')}
                                >
                                    + Add Book
                                </Button>
                            </div>
                            <div className="flex flex-row gap-x-4 items-center mb-4">
                                <div className="grid grid-cols-1 gap-y-2 w-1/3">
                                    <FormikControl
                                        id="books-searchQuery"
                                        name="filterQuery"
                                        placeholder="Search books..."
                                        value={formik.values.filterQuery}
                                        onChange={(e) => {
                                            formik.handleChange(e);
                                            filterBooks(books, e.target.value);
                                        }}
                                    />
                                </div>
                            </div>
                            {apiError ? <div className="text-mpLRed">{apiError}</div> :
                                hasLoaded &&
                                <Table
                                    tableName="books"
                                    data={filteredBooks}
                                    columns={getColumns()}
                                    defaultSorted={defaultSorted}
                                    emptyMessage="No books found"
                                />
                            }
                        </form>
                    )}
                </Formik>
            </div>
            <Socket refreshData={refreshData} page={pageTitle} setVersion={props.setVersion} />

            {showModal && (
                <BookModal
                    show={showModal}
                    mode={modalMode}
                    book={selectedBook}
                    onClose={handleCloseModal}
                    createBook={createBook}
                    updateBook={updateBook}
                    deleteBook={deleteBook}
                />
            )}
        </div>
    );
};

export default connect(null, { fetchBooksAll, createBook, updateBook, deleteBook })(Books);


